import { useRouter } from 'next/router';
import {
  TAXONOMY_LEVEL_2,
  TAXONOMY_LEVEL_3,
  TAXONOMY_LEVEL_4,
} from 'product-landing-pages/utils/taxonomyLevels';
import { slugify } from 'shared/utils/consumerSearchSlugify';
import { appendGOATandCountryCodeToTitle } from 'shared/utils/pageMetadataUtils';

const getBrandName = (filters, brandSlug) => {
  const defaultBrandName = brandSlug.replaceAll('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (!filters?.brandsList) {
    return defaultBrandName;
  }

  const brand = filters.brandsList?.find((b) => b.slug === brandSlug);

  if (brand) {
    return brand?.name;
  }

  return defaultBrandName;
};

export const usePageMetadataCS = ({
  mainTitle = '',
  defaultTitle = '',
  mainDescription = '',
  consumerSearchData = {},
}: {
  mainTitle?: string;
  defaultTitle?: string;
  mainDescription?: string;
  consumerSearchData?: any;
}) => {
  const { locale, pathname, query } = useRouter();
  let pageTitle = appendGOATandCountryCodeToTitle(mainTitle || defaultTitle, locale);
  let pageDescription = mainDescription;

  // Collections: /collections/[slug]
  if (pathname.includes('collections') && query?.slug) {
    const collectionName = query?.slug?.[0]
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(defaultTitle, locale);
    pageDescription =
      mainDescription ||
      `Shop the curated ${mainTitle || collectionName} collection on GOAT. Authenticity assured.`;
  }

  // Brands: /brand/[slug]
  if (pathname.includes('brand') && query?.slug) {
    const brandName = getBrandName(consumerSearchData?.aggregations?.filters, query?.slug);
    const title = `Buy ${brandName}: New & Iconic Styles`;
    pageTitle = appendGOATandCountryCodeToTitle(title, locale);
    pageDescription =
      mainDescription ||
      `Explore the past, present and future of ${brandName} on GOAT. Authenticity Assured.`;
  }

  // [pageSlug] paths: brand, color, silhouette, [...taxonomies], index
  if (query?.pageSlug && Object.keys(query).length > 1) {
    const category = (query.pageSlug as string)
      ?.replaceAll('sneakers', 'shoes')
      .replace(/-/g, ' ')
      .replace(/_/g, ' ');
    const [taxonomyLevel2, taxonomyLevel3, taxonomyLevel4] = Array.isArray(query?.taxonomies)
      ? query.taxonomies
      : [query?.taxonomies];

    const tax4Name =
      TAXONOMY_LEVEL_4?.find((tax) => taxonomyLevel4?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel4?.replace(/-/g, ' ');

    const tax3Name =
      TAXONOMY_LEVEL_3?.find((tax) => taxonomyLevel3?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel3?.replace(/-/g, ' ');

    const tax2Name =
      TAXONOMY_LEVEL_2?.find((tax) => taxonomyLevel2?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel2?.replace(/-/g, ' ');

    if (query?.brandName) {
      // /apparel/brand/supreme
      const brandName = getBrandName(consumerSearchData?.aggregations?.filters, query?.brandName);

      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle ||
          `Buy ${brandName} ${tax4Name || tax3Name || tax2Name || category} : New & Pre-Owned`,
        locale,
      );
      pageDescription =
        mainDescription ||
        `Explore the past, present and future of ${brandName} ${tax4Name || tax3Name || tax2Name || category} on GOAT. Authenticity Assured.`;
    } else if (query?.silhouette) {
      const normalizedSilhouetteValue = (query?.silhouette as string)
        ?.replace(/-/g, ' ')
        ?.replace(/_/g, ' ')
        ?.replace(/\b\w/g, (c) => c.toUpperCase());
      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle || `Buy ${normalizedSilhouetteValue}: New Releases & Iconic Styles`,
        locale,
      );
      pageDescription =
        mainDescription ||
        `Shop ${normalizedSilhouetteValue} on GOAT. Featuring new, upcoming and iconic styles. Authenticity assured.`;
    } else if (query?.colorValue) {
      const isShoes = category === 'shoes';
      const color = query?.colorValue as string;
      pageTitle = appendGOATandCountryCodeToTitle(mainTitle || `Buy ${color} ${category}`, locale);

      if (isShoes) {
        pageDescription =
          mainDescription ||
          `Shop ${color} ${category} on GOAT including iconic styles and upcoming and recent releases.`;
      } else {
        pageDescription =
          mainDescription ||
          `Shop ${color} ${category} on GOAT. Explore new releases & iconic styles. Authenticity assured.`;
      }
    } else if (taxonomyLevel2 === 'designer' || query?.designerValue) {
      // Designer: sneakers/designer/[designerValue]
      const designerName = taxonomyLevel3
        ?.replace(/-/g, ' ')
        ?.replace(/_/g, ' ')
        ?.replace(/\b\w/g, (c) => c.toUpperCase());
      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle || `Buy ${designerName} Shoes: New Releases & Iconic Styles`,
        locale,
      );
      pageDescription =
        mainDescription || `Shop shoes designed by ${designerName} on GOAT. Authenticity assured.`;
    } else {
      pageTitle = appendGOATandCountryCodeToTitle(
        mainTitle || `Buy ${tax4Name || tax3Name || tax2Name || category}: New & Pre-Owned`,
        locale,
      );
      pageDescription =
        mainDescription ||
        `Explore the past, present and future of ${tax4Name || tax3Name || tax2Name || category} on GOAT. Authenticity Assured.`;
    }
  } else if (query?.designerValue) {
    // Designer: sneakers/designer/[designerValue]
    const designerName = (query?.designerValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy ${designerName} Shoes: New Releases & Iconic Styles`,
      locale,
    );
    pageDescription =
      mainDescription || `Shop shoes designed by ${designerName} on GOAT. Authenticity assured.`;
  }
  // Upper Material: sneakers/upper-material/[upperMaterialValue]
  else if (query?.upperMaterialValue) {
    const upperMaterialName = (query?.upperMaterialValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy ${upperMaterialName} shoes`,
      locale,
    );
    pageDescription =
      mainDescription || `Shop ${upperMaterialName} shoes on GOAT. Authenticity assured.`;
  } else if (query?.technologyValue) {
    const technologyName = (query?.technologyValue as string)
      ?.replace(/-/g, ' ')
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy shoes featuring ${technologyName} technology`,
      locale,
    );
    pageDescription =
      mainDescription ||
      `Shop shoes featuring ${technologyName} technology on GOAT. Authenticity assured.`;
  } else {
    // Top level categories: /sneakers, /apparel, /bags

    const nameOfPath = pathname?.split('/');
    const [parentCategory, taxonomyLevel2, taxonomyLevel3, taxonomyLevel4] = nameOfPath;

    const tax4Name =
      TAXONOMY_LEVEL_4?.find((tax) => taxonomyLevel4?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel4?.replace(/-/g, ' ');

    const tax3Name =
      TAXONOMY_LEVEL_3?.find((tax) => taxonomyLevel3?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel3?.replace(/-/g, ' ');

    const tax2Name =
      TAXONOMY_LEVEL_2?.find((tax) => taxonomyLevel2?.toLowerCase() === slugify(tax)) ||
      taxonomyLevel2?.replace(/-/g, ' ');

    const categoryTodisplay = parentCategory === 'sneakers' ? 'shoes' : parentCategory;

    // Top Level Category with subcategory: /sneakers/basketball, /apparel/tops

    pageTitle = appendGOATandCountryCodeToTitle(
      mainTitle || `Buy ${tax4Name || tax3Name || tax2Name || categoryTodisplay} : New & Pre-Owned`,
      locale,
    );

    pageDescription =
      mainDescription ||
      `Explore the past, present and future of ${tax4Name || tax3Name || tax2Name || categoryTodisplay} on GOAT. Authenticity Assured.`;
  }

  // Timeline
  if (pathname.includes('timeline')) {
    const year = query?.timelineYear?.[0];

    if (year && year !== 'future') {
      pageTitle = appendGOATandCountryCodeToTitle(`${year} Sneaker Releases`, locale);
      pageDescription = `Shop sneaker releases from ${year} on GOAT. Authenticity assured.`;
    } else {
      pageTitle = appendGOATandCountryCodeToTitle('Sneaker Release Calendar', locale);
      pageDescription = `Discover a sneaker release calendar on GOAT. Featuring new & upcoming styles. Authenticity assured.`;
    }
  }

  return { pageTitle, pageDescription };
};
